/**
 * @fileoverview Modal component for viewing and editing patient details
 * Uses DmsDetailModal for consistent behavior across the application
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { intl, fetchData, HTTP_STATUS } from "dmsbasereact";
import { DmsDetailModal } from "../../DmsDetailModal";
import { ViewModeContent } from "./ViewModeContent";
import { EditModeContent } from "./EditModeContent";
import { normalizeGender, formatDateForInput } from "../../../utils/formatters";

/**
 * Prepares form data from patient object for editing
 */
const prepareEditFormData = (patient) => ({
  name: patient.name || "",
  cpf: patient.cpf || "",
  email: patient.email || "",
  phone: patient.phone || "",
  gender: normalizeGender(patient.gender),
  birth_date: formatDateForInput(patient.birth_date),
  has_pregnancy_potential: patient.has_pregnancy_potential || false,
  menstrual_cycle_regular: patient.menstrual_cycle_regular || false,
  status: patient.status,
  patient_status_reason_pk: patient.patient_status_reason_pk || null,
  status_reason: patient.status_reason || "",
  // Address fields
  zip_code: patient.address?.zip_code || "",
  address: patient.address?.address || "",
  number: patient.address?.number || "",
  city: patient.address?.city || "",
  state: patient.address?.state || "",
  // Fertility fields
  selectedContraceptiveMethod: patient._contraceptiveMethodPk || null,
  selectedInfertilityReason: patient._infertilityReasonPk || null,
  // Responsible Legal fields
  hasResponsibleLegal: !!patient.responsibleLegal,
  responsibleLegal_pk: patient.responsibleLegal?.pk || null,
  responsibleLegal_name: patient.responsibleLegal?.name || "",
  responsibleLegal_cpf: patient.responsibleLegal?.cpf || "",
  responsibleLegal_rg: patient.responsibleLegal?.rg || "",
  responsibleLegal_rg_issuing_agency: patient.responsibleLegal?.rg_issuing_agency || "",
  responsibleLegal_birth_date: formatDateForInput(patient.responsibleLegal?.birth_date),
  responsibleLegal_email: patient.responsibleLegal?.email || "",
  responsibleLegal_phone: patient.responsibleLegal?.phone || "",
  // Responsible Legal Address fields
  sameAddressAsPatient: false,
  responsibleLegal_zip_code: patient.responsibleAddress?.zip_code || "",
  responsibleLegal_address: patient.responsibleAddress?.address || "",
  responsibleLegal_number: patient.responsibleAddress?.number || "",
  responsibleLegal_city: patient.responsibleAddress?.city || "",
  responsibleLegal_state: patient.responsibleAddress?.state || "",
});

/**
 * Builds update payload from form data
 */
const buildUpdatePayload = (formData, patient) => {
  const birthDate = formData.birth_date
    ? new Date(formData.birth_date + "T00:00:00").toISOString()
    : null;

  const payload = {
    name: formData.name,
    cpf: formData.cpf,
    email: formData.email || null,
    phone: formData.phone?.replace(/\D/g, "") || null,
    gender: formData.gender,
    birth_date: birthDate,
    has_pregnancy_potential: formData.has_pregnancy_potential,
    menstrual_cycle_regular: formData.menstrual_cycle_regular,
    status: formData.status,
    address: {
      address_name: "P_" + formData.cpf,
      zip_code: formData.zip_code,
      address: formData.address,
      number: formData.number,
      city: formData.city,
      state: formData.state,
      country_code: "BR",
    },
  };

  // If status is changing, include reason fields
  if (formData.status !== patient.status) {
    payload.patient_status_reason_pk = formData.patient_status_reason_pk ? Number(formData.patient_status_reason_pk) : null;
    payload.status_reason = formData.status_reason || null;
  }

  // Include responsible legal data if enabled and has pk (existing)
  if (formData.hasResponsibleLegal && formData.responsibleLegal_pk) {
    const rlBirthDate = formData.responsibleLegal_birth_date
      ? new Date(formData.responsibleLegal_birth_date + "T00:00:00").toISOString()
      : null;

    payload.responsible_legal = {
      pk: formData.responsibleLegal_pk,
      name: formData.responsibleLegal_name,
      cpf: formData.responsibleLegal_cpf?.replace(/\D/g, "") || "",
      rg: formData.responsibleLegal_rg || null,
      rg_issuing_agency: formData.responsibleLegal_rg_issuing_agency || null,
      birth_date: rlBirthDate,
      email: formData.responsibleLegal_email || null,
      phone: formData.responsibleLegal_phone?.replace(/\D/g, "") || null,
    };

    // Include responsible legal address if not same as patient
    if (!formData.sameAddressAsPatient) {
      payload.responsible_legal.address = {
        address_name: "RL_" + (formData.responsibleLegal_cpf?.replace(/\D/g, "") || ""),
        zip_code: formData.responsibleLegal_zip_code || "",
        address: formData.responsibleLegal_address || "",
        number: formData.responsibleLegal_number || "",
        city: formData.responsibleLegal_city || "",
        state: formData.responsibleLegal_state || "",
        country_code: "BR",
      };
    }
  }

  return payload;
};

/**
 * Modal for viewing and editing patient details
 */
export const PatientDetailModal = ({
  open,
  onClose,
  patient = null,
  onSave = null,
  table = null}) => {
  const [loading, setLoading] = useState(false);
  const [enrichedPatient, setEnrichedPatient] = useState(null);
  const [patientStatusReasons, setPatientStatusReasons] = useState([]);
  // Store original status when modal opens to ensure correct comparison
  const [originalStatus, setOriginalStatus] = useState(null);

  // Capture original status when modal opens
  useEffect(() => {
    if (open && patient) {
      setOriginalStatus(patient.status);
    } else if (!open) {
      setOriginalStatus(null);
    }
  }, [open, patient]);

  // Fetch patient status reasons on mount
  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const response = await fetchData('/patient_status_reason/all', 'GET', true);
        if (response.status === HTTP_STATUS.OK_200) {
          // Handle different response formats
          let reasonsData = [];
          if (response.data?.data) {
            reasonsData = Array.isArray(response.data.data) ? response.data.data : 
                         (response.data.data?.rows || []);
          } else if (Array.isArray(response.data)) {
            reasonsData = response.data;
          }
          setPatientStatusReasons(reasonsData);
        }
      } catch (error) {
        console.error('Error fetching patient status reasons:', error);
        setPatientStatusReasons([]);
      }
    };
    fetchReasons();
  }, []);

  // Load additional patient data
  const loadAdditionalData = useCallback(async (patientData) => {
    if (!patientData) return null;

    let fullPatientData = { ...patientData };
    let responsibleLegal = null;
    let responsibleAddress = null;
    let contraceptiveMethod = null;
    let infertilityReason = null;

    // Fetch full patient details (includes patient_status_reason)
    try {
      const detailsRes = await fetchData(`/patient/${patientData.pk}/details`, 'GET', true);
      if (detailsRes.status === HTTP_STATUS.OK_200) {
        fullPatientData = { ...patientData, ...detailsRes.data };
      }
    } catch (e) {
      // Ignore errors, use original data
    }

    // Fetch responsible legal
    try {
      const res = await fetchData(`/patient_responsible_legal/get_responsible_legal/${patientData.pk}`, 'GET', true);
      if (res.status === HTTP_STATUS.OK_200 && res.data.data) {
        responsibleLegal = res.data.data;
        if (res.data.data.address_pk) {
          const addressRes = await fetchData(`/address/${res.data.data.address_pk}`, 'GET', true);
          if (addressRes?.data?.data) {
            responsibleAddress = addressRes.data.data;
          }
        }
      }
    } catch (e) {
      // Ignore errors
    }

    // Fetch reproductive health data (female only)
    const gender = String(patientData.gender || "").toUpperCase();
    let contraceptiveMethodPk = null;
    let infertilityReasonPk = null;

    if (gender === "F") {
      try {
        if (!patientData.has_pregnancy_potential) {
          const res = await fetchData(`/patient_infertility_reason/get_infertility_reason/${patientData.pk}`, "GET", true);
          if (res.status === HTTP_STATUS.OK_200) {
            infertilityReason = res.data.data.reason;
            infertilityReasonPk = res.data.data.pk;
          }
        } else {
          const res = await fetchData(`/patient_contraceptive_method/get_contraceptive_method/${patientData.pk}`, "GET", true);
          if (res.status === HTTP_STATUS.OK_200) {
            contraceptiveMethod = res.data.data.reason;
            contraceptiveMethodPk = res.data.data.pk;
          }
        }
      } catch (e) {
        // Ignore errors
      }
    }

    return {
      ...fullPatientData,
      responsibleLegal,
      responsibleAddress,
      contraceptiveMethod,
      infertilityReason,
      _contraceptiveMethodPk: contraceptiveMethodPk,
      _infertilityReasonPk: infertilityReasonPk,
    };
  }, []);

  // Load data when modal opens or patient changes
  useEffect(() => {
    if (open && patient) {
      setLoading(true);
      loadAdditionalData(patient).then((enriched) => {
        setEnrichedPatient(enriched);
        setLoading(false);
      });
    } else if (!open) {
      setEnrichedPatient(null);
    }
  }, [open, patient, loadAdditionalData]);

  const handleDataUpdated = useCallback(() => {
    table?.current?.updateTable();
  }, [table]);

  // Create a buildPayload wrapper that uses the original status for comparison
  const buildPayloadWrapper = useCallback((formData, originalData) => {
    const comparisonPatient = { ...originalData, status: originalStatus };
    return buildUpdatePayload(formData, comparisonPatient);
  }, [originalStatus]);

  // Custom save handler that also saves fertility data and responsible legal
  const handleSave = useCallback(async (patientPk, payload, formData) => {
    // Handle creation of new responsible legal (if hasResponsibleLegal but no pk)
    if (formData.hasResponsibleLegal && !formData.responsibleLegal_pk) {
      const rlBirthDate = formData.responsibleLegal_birth_date
        ? new Date(formData.responsibleLegal_birth_date + "T00:00:00").toISOString()
        : null;

      // Build address data
      let addressData;
      if (formData.sameAddressAsPatient) {
        addressData = {
          address_name: "RL_" + (formData.responsibleLegal_cpf?.replace(/\D/g, "") || ""),
          zip_code: formData.zip_code || "",
          address: formData.address || "",
          number: formData.number || "",
          city: formData.city || "",
          state: formData.state || "",
          country_code: "BR",
        };
      } else {
        addressData = {
          address_name: "RL_" + (formData.responsibleLegal_cpf?.replace(/\D/g, "") || ""),
          zip_code: formData.responsibleLegal_zip_code || "",
          address: formData.responsibleLegal_address || "",
          number: formData.responsibleLegal_number || "",
          city: formData.responsibleLegal_city || "",
          state: formData.responsibleLegal_state || "",
          country_code: "BR",
        };
      }

      const rlData = {
        name: formData.responsibleLegal_name,
        cpf: formData.responsibleLegal_cpf?.replace(/\D/g, "") || "",
        rg: formData.responsibleLegal_rg || null,
        rg_issuing_agency: formData.responsibleLegal_rg_issuing_agency || null,
        birth_date: rlBirthDate,
        email: formData.responsibleLegal_email || null,
        phone: formData.responsibleLegal_phone?.replace(/\D/g, "") || null,
        address: addressData,
      };

      try {
        // Create responsible legal
        const rlRes = await fetchData("/responsible_legal", "POST", true, rlData);
        if (rlRes.status === HTTP_STATUS.CREATED_201) {
          const rlPk = rlRes.data.data.pk;
          // Create link between patient and responsible legal
          await fetchData("/patient_responsible_legal", "POST", true, {
            patient_pk: patientPk,
            responsible_legal_pk: rlPk,
          });
        }
      } catch (error) {
        console.error("Error creating responsible legal:", error);
      }
    }

    // Save the patient data using the original onSave
    const result = await onSave(patientPk, payload);

    // Save fertility data if patient is female
    if (formData && formData.gender === "F") {
      if (formData.has_pregnancy_potential && formData.selectedContraceptiveMethod) {
        try {
          await fetchData(
            `/patient_contraceptive_method/update_contraceptive_method/${patientPk}?contraceptive_method_pk=${formData.selectedContraceptiveMethod}`,
            'PUT',
            true
          );
        } catch (error) {
          try {
            await fetchData("/patient_contraceptive_method", "POST", true, {
              patient_pk: patientPk,
              contraceptive_method_pk: formData.selectedContraceptiveMethod
            });
          } catch (postError) {
            console.error("Error saving contraceptive method:", postError);
          }
        }
      }

      if (!formData.has_pregnancy_potential && formData.selectedInfertilityReason) {
        try {
          await fetchData(
            `/patient_infertility_reason/update_infertility_reason/${patientPk}?infertility_reason_pk=${formData.selectedInfertilityReason}`,
            'PUT',
            true
          );
        } catch (error) {
          try {
            await fetchData("/patient_infertility_reason", "POST", true, {
              patient_pk: patientPk,
              infertility_reason_pk: formData.selectedInfertilityReason
            });
          } catch (postError) {
            console.error("Error saving infertility reason:", postError);
          }
        }
      }
    }

    // Reload enriched data after save
    if (result) {
      const enrichedResult = await loadAdditionalData(result);
      setEnrichedPatient(enrichedResult);
      return enrichedResult;
    }

    return result;
  }, [onSave, loadAdditionalData]);

  return (
    <DmsDetailModal
      open={open}
      onClose={onClose}
      data={enrichedPatient}
      loading={loading}
      title={{
        view: intl.get("PatientDetails").d("Patient Details"),
        edit: intl.get("EditPatientDetails").d("Edit Patient Details"),
      }}
      viewContent={ViewModeContent}
      editContent={(props) => <EditModeContent {...props} patientStatusReasons={patientStatusReasons} originalPatient={{ ...patient, status: originalStatus }} />}
      prepareFormData={prepareEditFormData}
      buildPayload={buildPayloadWrapper}
      onSave={onSave ? handleSave : null}
      onDataUpdated={handleDataUpdated}
      confirmMessage={intl.get("ConfirmSavePatientMessage").d("Are you sure you want to save the patient changes?")}
    />
  );
};

PatientDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patient: PropTypes.object,
  onSave: PropTypes.func,
  table: PropTypes.object,
};

